import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@apollo/client/react";
import { WAREHOUSE_LOCATIONS_DETAILS } from "src/constants/graphql/queries";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";

const styles = {
  dialogTitle: {
    position: "relative",
    textAlign: "center",
    pb: 1.5,
  },
  closeButton: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  dialogContent: {
    pt: 0.5,
  },
  skeletonText: {
    mb: 0.5,
  },
  skeletonRectangular: {
    borderRadius: 999,
  },
  progressBar: theme => ({
    height: 2,
    borderRadius: 999,
    backgroundColor: theme.palette.action.hover,
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#90caf9",
    },
  }),
};

export function buildWarehouseLocationEntries(details = {}, variant = "empty") {
  const empty = variant === "empty";

  // Helper to calculate percentage for each location type
  const calcPercent = (emptyCount, occupiedCount) => {
    const total = emptyCount + occupiedCount;
    if (total === 0) return 0;
    const value = empty ? emptyCount : occupiedCount;
    return Math.round((value / total) * 100);
  };

  // Warehouse occupancy widgets report base (non-mobile) locations only.
  // Format: [label, count, percentage]
  const list = [
    [
      "Vaults",
      empty ? details.vaultEmpty ?? 0 : details.vaultOccupied ?? 0,
      calcPercent(details.vaultEmpty ?? 0, details.vaultOccupied ?? 0),
    ],
    [
      "Trailer",
      empty ? details.trailerEmpty ?? 0 : details.trailerOccupied ?? 0,
      calcPercent(details.trailerEmpty ?? 0, details.trailerOccupied ?? 0),
    ],
    [
      "Rack",
      empty ? details.rackEmpty ?? 0 : details.rackOccupied ?? 0,
      calcPercent(details.rackEmpty ?? 0, details.rackOccupied ?? 0),
    ],
    [
      "Floor",
      empty ? details.floorEmpty ?? 0 : details.floorOccupied ?? 0,
      calcPercent(details.floorEmpty ?? 0, details.floorOccupied ?? 0),
    ],
    [
      "Sofa Rack",
      empty ? details.sofaRackEmpty ?? 0 : details.sofaRackOccupied ?? 0,
      calcPercent(details.sofaRackEmpty ?? 0, details.sofaRackOccupied ?? 0),
    ],
  ];
  // Filter out zero values for both empty and occupied modals
  return list.filter(([, count]) => count > 0);
}

export default function WarehouseLocationsInfoDialog({
  open,
  title,
  warehouseId,
  variant = "empty",
  onClose,
}) {
  const { data, loading } = useQuery(WAREHOUSE_LOCATIONS_DETAILS, {
    variables: { warehouseId },
    skip: !warehouseId,
    fetchPolicy: "cache-and-network",
  });
  const details = data?.warehouseLocationsDetails || {};
  const entries = useMemo(() => buildWarehouseLocationEntries(details, variant), [
    details,
    variant,
  ]);
  const isEmpty = variant === "empty";
  const total = isEmpty
    ? details.totalEmptyBaseLocations || 0
    : details.totalOccupiedBaseLocations || 0;
  const emptyStateText =
    variant === "empty" ? "No empty base locations" : "No occupied base locations";

  const formatNumber = value => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toLocaleString() : "0";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle id="warehouse-locations-info-title" sx={styles.dialogTitle}>
        <Typography fontSize={20} fontWeight={500} align="center" color="text.primary">
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close" sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        {loading ? (
          <Box>
            {[0, 1, 2, 3].map(key => (
              <Box key={key} py={0.75}>
                <Skeleton variant="text" width="40%" height={18} sx={styles.skeletonText} />
                <Skeleton variant="rectangular" height={8} sx={styles.skeletonRectangular} />
              </Box>
            ))}
          </Box>
        ) : entries.length === 0 ? (
          <Box color="text.secondary">{emptyStateText}</Box>
        ) : (
          <Box>
            {entries.map(([label, count, percent]) => {
              return (
                <Box key={label} py={0.75}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="baseline"
                    mb={0.75}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography color="text.primary">{label}</Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {percent}% {isEmpty ? "empty" : "occupied"}
                      </Typography>
                    </Box>
                    <Typography color="text.primary">{formatNumber(count)}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percent}
                    sx={theme => ({
                      ...styles.progressBar(theme),
                    })}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
